/**
 * Right-rail widgets: trending hashtags + suggested people + nearby map + friends.
 *
 * These are global dashboard right-rail content (rendered across dashboard
 * pages). A Follow / Add action on suggested people is OUT OF SCOPE for #4 —
 * each person is a link only. TODO(#5): add the follow/add-friend action.
 *
 * The "Who's near you" widget renders a MapLibre GL map (OpenFreeMap
 * "liberty" vector tiles) with avatar pins for each nearby user.
 * `useGoogleMaps` still gates the lazy-mount pattern below — its `'ready'`
 * state now fires immediately since MapLibre is a static import — and the
 * list-fallback path stays wired for the disabled / not-yet-idle window.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { Link } from 'react-router-dom';
import { ChevronRight, Clock, Hash, MapPin, Sparkles, TrendingUp, Tv, Users } from 'lucide-react';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  UserAvatar,
} from '@/components/ui';
import { useDiscoverEvents, useNearbyEvents, useTrendingHashtags, useUserList } from '@/lib/api';
import { useViewerGeo } from '@/lib/api/hooks/useViewerGeo';
import type { PlaceEvent } from '@/types/api';
import { EventsList } from '@/pages/events/components/EventsList';
import { EventCard } from '@/pages/events/EventsDiscovery';
import { YaariplayWidget } from '@/components/home/YaariplayWidget';
import { useNearbyUsers } from '@/lib/api/hooks/useNearbyUsers';
import { useUserFriends } from '@/pages/profile/hooks/useProfile';
import { genderEmoji } from '@/pages/profile/lib/profile';
import { useGoogleMaps } from '@/pages/places/hooks/useGoogleMaps';
import { useAuth } from '@/pages/auth';
import { AdsenseSlot } from '@/components/ads/AdsenseSlot';
import { AdSlots } from '@/lib/adSlots';
import { track, EVENTS } from '@/lib/analytics';
import type { User } from '@/types/api';

function initials(user?: Partial<User>): string {
  const f = user?.firstname?.[0] ?? '';
  const l = user?.lastname?.[0] ?? '';
  return (f + l).toUpperCase() || '?';
}

/** Strip a leading `#` from a hashtag name for the route + display. */
function bareTag(name: string): string {
  return name.replace(/^#/, '');
}

/**
 * Build the "City, State, Country" subtitle used under names in the people
 * rail widgets. Empty fields are skipped; when `city === state` (case-
 * insensitive) the duplicate state is collapsed so a row reads
 * "Singapore, Singapore" instead of "Singapore, Singapore, Singapore". A
 * user with only a country still gets a one-token line.
 */
function formatLocation(u: Partial<User>): string {
  const city = (u.city ?? '').trim();
  const state = (u.state ?? '').trim();
  const country = (u.country ?? '').trim();
  const dedupState = city && state && city.toLowerCase() === state.toLowerCase();
  return [city, dedupState ? '' : state, country].filter(Boolean).join(', ');
}

/**
 * Build the "Gender, Age, Relationship" line shown under each name in the
 * people rail widgets. Prefers the API's pre-formatted `extra.*` strings
 * (the user-list endpoint sets these to display-ready labels — e.g.
 * `extra.relationship_status = "Single"` while the top-level field is a
 * numeric id). Falls back to raw `gender` when `extra` isn't populated.
 * Empty parts are dropped so the line gracefully shortens. Returns the
 * gender emoji separately so the caller can render it with its own
 * spacing/colour.
 */
function formatProfileMeta(u: Partial<User>): { emoji: string; text: string } {
  const x = u.extra ?? {};
  const gender = String(x.gender ?? u.gender ?? '').trim();
  const age = String(x.age ?? '').trim();
  const rel = String(x.relationship_status ?? '').trim();
  return {
    emoji: gender ? genderEmoji(gender) : '',
    text: [gender, age, rel].filter(Boolean).join(', '),
  };
}

export function TrendingHashtags() {
  const { data, isLoading, isError } = useTrendingHashtags();
  const tags = data?.list ?? [];

  if (isError) {
    return (
      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-4 w-4 text-brand-700" /> Trending
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 pt-1">
          <p className="px-2 py-1 text-sm text-muted-foreground">
            Couldn&apos;t load right now.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingUp className="h-4 w-4 text-brand-700" /> Trending
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 p-2 pt-1">
        {isLoading ? (
          [0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="mx-2 my-2 h-3 w-3/4" />
          ))
        ) : tags.length === 0 ? (
          <p className="px-2 py-1 text-sm text-muted-foreground">
            No trends yet.
          </p>
        ) : (
          tags.map((t) => {
            const tag = bareTag(t.name);
            return (
              <Link
                key={t.ID}
                to={`/hashtag/${tag}`}
                onClick={() =>
                  track(EVENTS.HASHTAG_CLICKED, {
                    tag,
                    source: 'trending_widget',
                    success: true,
                  })
                }
                className="flex items-center justify-between rounded-md px-2 py-1.5 transition-colors hover:bg-surface-2"
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  <Hash className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm font-medium text-ink">
                    {tag}
                  </span>
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {t.post_count} {t.post_count === 1 ? 'post' : 'posts'}
                </span>
              </Link>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

export function SuggestedPeople() {
  // Fetch the larger limit (10) used by the mobile carousel so the
  // TanStack cache key matches across both surfaces; slice locally
  // for the rail's 5-up view. Misaligned limits previously fragmented
  // the cache → tablet rotations forced a fresh REST round-trip.
  const { data, isLoading, isError } = useUserList({
    type: 'suggested',
    limit: 10,
  });
  const people = (data?.list ?? []).slice(0, 5);

  if (isError) {
    return (
      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-brand-700" /> Suggested for you
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 pt-1">
          <p className="px-2 py-1 text-sm text-muted-foreground">
            Couldn&apos;t load right now.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-brand-700" /> Suggested for you
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 p-2 pt-1">
        {isLoading ? (
          [0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 px-2 py-2">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))
        ) : people.length === 0 ? (
          <p className="px-2 py-1 text-sm text-muted-foreground">
            No suggestions right now.
          </p>
        ) : (
          people.map((u) => (
            <Link
              key={u.ID}
              to={u.username ? `/user/${u.username}` : '#'}
              className="flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-surface-2"
            >
              <UserAvatar
                src={u.image}
                alt={`${u.firstname} ${u.lastname}`}
                fallback={initials(u)}
                plan={u.plan}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink">
                  {`${u.firstname} ${u.lastname}`.trim() || 'Member'}
                </p>
                {(() => {
                  const meta = formatProfileMeta(u);
                  const location = formatLocation(u);
                  return (
                    <>
                      {meta.text ? (
                        <p className="truncate text-xs text-muted-foreground">
                          {meta.emoji ? (
                            <span aria-hidden="true">{meta.emoji} </span>
                          ) : null}
                          {meta.text}
                        </p>
                      ) : null}
                      {location ? (
                        <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
                          <span className="truncate">{location}</span>
                        </p>
                      ) : null}
                    </>
                  );
                })()}
              </div>
              {/* TODO(#5): Follow / Add friend action button. */}
            </Link>
          ))
        )}
      </CardContent>
    </Card>
  );
}

export function LastLoggedUsers() {
  // `live=Y` tells the backend to attach a `live: boolean` to each row and
  // sort live-streamers first — parity with legacy
  // `user/list?type=latest&live=Y` from REACTJS Dashboard.jsx. Refetch every
  // 30s so a broadcaster going on/off air shows up without a page reload —
  // legacy didn't auto-refresh either, but the V2 broadcaster sits inside
  // the same SPA so users expect the rail to track their own state.
  //
  // `limit: 12` matches `MobileLatestUsers` (the < lg horizontal carousel)
  // so the TanStack cache key — `(type, limit, live)` — is identical.
  // Resizing across the 1024 boundary mid-session swaps which component is
  // mounted, but the data is already cached so there's no duplicate fetch
  // or visible loading flash. This card only renders the first 5 rows.
  const { data, isLoading, isError } = useUserList(
    { type: 'latest', limit: 12, live: 'Y' },
    { refetchInterval: 30_000, staleTime: 15_000 },
  );
  const people = (data?.list ?? []).slice(0, 5);

  if (isError) {
    return (
      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-4 w-4 text-brand-700" /> Recently active
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 pt-1">
          <p className="px-2 py-1 text-sm text-muted-foreground">
            Couldn&apos;t load right now.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4 text-brand-700" /> Recently active
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 p-2 pt-1">
        {isLoading ? (
          [0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 px-2 py-2">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))
        ) : people.length === 0 ? (
          <p className="px-2 py-1 text-sm text-muted-foreground">
            No one has logged in recently.
          </p>
        ) : (
          people.map((u) => {
            const isLive = u.live === true;
            // Live users deep-link straight to the watch surface; everyone
            // else lands on the standard profile.
            const to = u.username
              ? isLive
                ? `/user/${u.username}/livestream`
                : `/user/${u.username}`
              : '#';
            return (
              <Link
                key={u.ID}
                to={to}
                className="flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-surface-2"
                aria-label={
                  isLive
                    ? `Watch ${u.firstname} ${u.lastname}'s live stream`
                    : undefined
                }
              >
                <span className="relative shrink-0">
                  {/* Live ring: border applied directly to the Avatar
                      root span (which IS the span immediately wrapping
                      the <img>). Box-sizing: border-box keeps the
                      element square so `rounded-full` traces a true
                      circle. `border` is Tailwind's default 1px. */}
                  <UserAvatar
                    src={u.image}
                    alt={`${u.firstname} ${u.lastname}`}
                    fallback={initials(u)}
                    plan={u.plan}
                    className={isLive ? 'border border-red-500' : undefined}
                  />
                  {isLive ? (
                    <span
                      // `top-0 -translate-y-1/2` matches MobileLatestUsers
                      // so both Live pills sit at the same visual peek
                      // height (half above the avatar). Was `-top-1`
                      // (only 4px peek) → looked offset against the
                      // mobile carousel. (I15 fix.)
                      className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-sm bg-red-500 px-1 py-px text-[9px] font-bold uppercase leading-none tracking-wide text-white shadow-sm"
                      aria-hidden="true"
                    >
                      Live
                    </span>
                  ) : null}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">
                    {`${u.firstname} ${u.lastname}`.trim() || 'Member'}
                  </p>
                  {isLive ? (
                    <p className="truncate text-xs text-muted-foreground">
                      <span className="font-medium text-red-500">
                        ● Streaming now
                      </span>
                    </p>
                  ) : (() => {
                    const location = formatLocation(u);
                    return location ? (
                      <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
                        <span className="truncate">{location}</span>
                      </p>
                    ) : null;
                  })()}
                </div>
              </Link>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Who's near you — Google Map with avatar pins, list fallback                 */
/* -------------------------------------------------------------------------- */

/**
 * Compact list row used by the WhosNearYou fallback (and as a safe degraded
 * surface when Google Maps is unavailable). Renders avatar + name + city.
 */
function NearbyRow({ u }: { u: User }) {
  return (
    <Link
      to={u.username ? `/user/${u.username}` : '#'}
      className="flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-surface-2"
    >
      <UserAvatar
        src={u.image}
        alt={`${u.firstname} ${u.lastname}`}
        fallback={initials(u)}
        plan={u.plan}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink">
          {`${u.firstname} ${u.lastname}`.trim() || 'Member'}
        </p>
        {u.city || u.country ? (
          <p className="truncate text-xs text-muted-foreground">
            {[u.city, u.country].filter(Boolean).join(', ')}
          </p>
        ) : u.username ? (
          <p className="truncate text-xs text-muted-foreground">@{u.username}</p>
        ) : null}
      </div>
    </Link>
  );
}

/** OpenFreeMap "liberty" style — same vector tile source as the Places
 *  MapView so both surfaces share tile cache behaviour. */
const OPENFREEMAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';

/**
 * Renders a small MapLibre GL map with avatar pins for each nearby user.
 * Each pin is a plain DOM element (avatar `<img>` inside a rounded ring) so
 * we don't have to ship raster assets per user. Falls back to a list when
 * the map layer isn't ready or markers don't have valid coords.
 *
 * Uses OpenFreeMap's free unlimited "liberty" vector tiles — no API key,
 * production-safe (osm.org 403s prod traffic).
 */
function NearbyMap({
  users,
  origin,
}: {
  users: User[];
  origin: { lat: number; lng: number };
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);

  useEffect(() => {
    if (!ref.current) return;
    // MapLibre uses [lng, lat] — opposite of Leaflet's [lat, lng].
    if (!mapRef.current) {
      mapRef.current = new maplibregl.Map({
        container: ref.current,
        style: OPENFREEMAP_STYLE_URL,
        center: [origin.lng, origin.lat],
        zoom: 5,
        scrollZoom: false,
        attributionControl: { compact: true },
      });
      mapRef.current.addControl(
        new maplibregl.NavigationControl({ showCompass: false }),
        'top-right',
      );
    } else {
      mapRef.current.setCenter([origin.lng, origin.lat]);
    }

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    // Track lng/lat bounds manually — MapLibre's LngLatBounds accepts
    // extend([lng,lat]) but we compute inline so we can guard on the
    // "did we extend at least one" flag cheaply.
    let minLng = origin.lng;
    let maxLng = origin.lng;
    let minLat = origin.lat;
    let maxLat = origin.lat;
    let extended = false;

    for (const u of users) {
      const lat = Number(u.map_lat);
      const lng = Number(u.map_lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const name =
        `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim() || 'Member';
      const avatar = u.image
        ? `<img src="${String(u.image).replace(/"/g, '&quot;')}" alt="" style="width:100%;height:100%;object-fit:cover;display:block;" />`
        : `<span style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;background:#e5e7eb;color:#374151;font-size:11px;font-weight:600;">${(initials({
            firstname: u.firstname,
            lastname: u.lastname,
          }) || '?').replace(/</g, '&lt;')}</span>`;
      const el = document.createElement('div');
      el.className = 'yr-nearby-user';
      el.title = name;
      el.style.width = '28px';
      el.style.height = '28px';
      el.innerHTML =
        '<span style="display:block;width:28px;height:28px;border-radius:9999px;overflow:hidden;border:2px solid #FFFFFF;box-shadow:0 1px 3px rgba(0,0,0,0.25);background:#e5e7eb;">' +
        avatar +
        '</span>';
      const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([lng, lat])
        .addTo(mapRef.current!);
      markersRef.current.push(marker);
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      extended = true;
    }

    if (extended) {
      try {
        mapRef.current.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          { padding: 20, maxZoom: 13, duration: 0 },
        );
      } catch {
        /* ignore — fitBounds can throw on degenerate bounds */
      }
    }

    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
    };
  }, [users, origin]);

  // Tear down the MapLibre map itself on unmount so a route navigation
  // doesn't leak the WebGL context / tile-fetch listeners.
  useEffect(() => {
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  return <div ref={ref} className="h-[220px] w-full rounded-md bg-surface-2" />;
}

/**
 * "Who's near you" right-rail card. Pulls the signed-in user's saved
 * `map_lat`/`map_lng` from the auth store, falls back to `navigator.geolocation`
 * (best-effort, no prompt nag — `maximumAge` accepts cached coords).
 */
export function WhosNearYou() {
  const user = useAuth((s) => s.user);
  // Defer the ~120 kB Google Maps script until the browser is idle.
  // The avatar list still renders without Maps (falls through to the
  // `people.length` fallback) — only the inline mini-map needs it.
  // Pushing the load off the critical path keeps /dashboard LCP clean
  // for users who never scroll down to this widget.
  const [mapsAllowed, setMapsAllowed] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const ric = (window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;
    if (typeof ric === 'function') {
      ric(() => setMapsAllowed(true), { timeout: 4000 });
    } else {
      window.setTimeout(() => setMapsAllowed(true), 2000);
    }
  }, []);
  const { status: mapsStatus } = useGoogleMaps({ enabled: mapsAllowed });

  // Opportunistic geolocation — never prompts noisily; if the browser already
  // has cached coords (or the user has granted permission elsewhere) we use
  // them. Skipped when the auth user already has `map_lat`/`map_lng`.
  const [geo, setGeo] = useState<{ lat: number; lng: number } | null>(null);
  const haveAuthCoords =
    typeof user?.map_lat === 'number' &&
    typeof user?.map_lng === 'number' &&
    Number.isFinite(user.map_lat) &&
    Number.isFinite(user.map_lng);

  useEffect(() => {
    if (haveAuthCoords) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    let active = true;
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!active) return;
          setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        () => {
          /* ignore — graceful fallback */
        },
        { maximumAge: 10 * 60 * 1000, timeout: 5000 },
      );
    } catch {
      /* some browsers throw on insecure context — ignore */
    }
    return () => {
      active = false;
    };
  }, [haveAuthCoords]);

  const origin = useMemo<{ lat: number; lng: number } | null>(() => {
    if (haveAuthCoords) {
      return { lat: Number(user!.map_lat), lng: Number(user!.map_lng) };
    }
    return geo;
  }, [haveAuthCoords, user, geo]);

  const { data, isLoading, isError } = useNearbyUsers({
    lat: origin?.lat,
    lng: origin?.lng,
    limit: 8,
  });
  const people = data?.list ?? [];

  if (isError) {
    return (
      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="h-4 w-4 text-brand-700" /> Who&apos;s near you
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 pt-1">
          <p className="px-2 py-1 text-sm text-muted-foreground">
            Couldn&apos;t load right now.
          </p>
        </CardContent>
      </Card>
    );
  }

  // No coords at all — hide rather than render an empty card. Distinct from
  // the isError branch above: this is "user has no usable location" (auth
  // store empty + browser geolocation denied/unavailable), not "network
  // failure" — there's no recoverable state to surface, so hiding is the
  // honest choice.
  if (!origin && !isLoading) return null;

  const canRenderMap =
    mapsStatus === 'ready' && origin && people.length > 0;

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <MapPin className="h-4 w-4 text-brand-700" /> Who&apos;s near you
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 p-2 pt-1">
        {isLoading ? (
          <div className="space-y-2 px-2 py-2">
            <Skeleton className="h-[160px] w-full rounded-md" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        ) : people.length === 0 ? (
          <p className="px-2 py-1 text-sm text-muted-foreground">
            No one nearby right now.
          </p>
        ) : canRenderMap ? (
          <div className="space-y-2 p-2">
            <NearbyMap users={people} origin={origin!} />
            <div className="flex -space-x-2 overflow-hidden">
              {people.slice(0, 5).map((u) => (
                <Link
                  key={u.ID}
                  to={u.username ? `/user/${u.username}` : '#'}
                  title={`${u.firstname ?? ''} ${u.lastname ?? ''}`.trim()}
                  className="inline-block"
                >
                  <UserAvatar
                    src={u.image}
                    alt={`${u.firstname} ${u.lastname}`}
                    fallback={initials(u)}
                    plan={u.plan}
                    className="h-8 w-8 ring-2 ring-background"
                  />
                </Link>
              ))}
            </div>
          </div>
        ) : (
          // Maps unavailable (no-key / error / not-ready) — degrade to list.
          people.slice(0, 5).map((u) => <NearbyRow key={u.ID} u={u} />)
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Friends list (auth-gated)                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Right-rail Friends card — shows up to 5 of the signed-in user's friends.
 * Renders nothing when the viewer is signed out (DashboardLayout already
 * gates the rail variant, but we double-gate so a stray render never errors).
 */
export function FriendsRail() {
  const user = useAuth((s) => s.user);
  const userId = user?.ID;
  const { data, isLoading, isError } = useUserFriends(userId, '');
  const friends = (data?.list ?? []).slice(0, 5);

  if (!userId) return null;
  if (isError) {
    return (
      <Card>
        <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4 text-brand-700" /> Friends
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 pt-1">
          <p className="px-2 py-1 text-sm text-muted-foreground">
            Couldn&apos;t load right now.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-4 w-4 text-brand-700" /> Friends
        </CardTitle>
        <Link
          to="/dashboard/friends"
          className="text-xs font-medium text-brand-700 hover:underline"
        >
          View all
        </Link>
      </CardHeader>
      <CardContent className="space-y-1 p-2 pt-1">
        {isLoading ? (
          [0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 px-2 py-2">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))
        ) : friends.length === 0 ? (
          <p className="px-2 py-1 text-sm text-muted-foreground">
            No friends yet — add some.
          </p>
        ) : (
          friends.map((u) => (
            <Link
              key={u.ID}
              to={u.username ? `/user/${u.username}` : '#'}
              className="flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-surface-2"
            >
              <UserAvatar
                src={u.image}
                alt={`${u.firstname} ${u.lastname}`}
                fallback={initials(u)}
                plan={u.plan}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink">
                  {`${u.firstname} ${u.lastname}`.trim() || 'Member'}
                </p>
                {u.username ? (
                  <p className="truncate text-xs text-muted-foreground">
                    @{u.username}
                  </p>
                ) : null}
              </div>
            </Link>
          ))
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Small highlighted Live TV CTA pinned to the top of the dashboard right rail
 * (above "Recently active"). Replaces the now-removed Live TV link in the
 * public top-nav — the goal is to keep Live TV discoverable from the
 * dashboard without crowding the main horizontal navigation. Red dot +
 * animated ping signals "live"; chevron suggests entering a sub-experience.
 */
export function LiveTVCta() {
  return (
    <Link
      to="/live-tv"
      aria-label="Browse channels"
      className="group flex items-center justify-between gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-rose-700 shadow-sm transition-colors hover:bg-rose-100"
    >
      <span className="flex items-center gap-2 text-sm font-semibold">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-500 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-600" />
        </span>
        <Tv className="h-4 w-4" />
        Channels
      </span>
      <ChevronRight className="h-4 w-4 text-rose-600 transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

interface RightRailWidgetsProps {
  /** Render the Trending hashtags card. Defaults to `true`. */
  showTrending?: boolean;
  /** Render the Suggested people card. Defaults to `true`. */
  showSuggested?: boolean;
  /** Render the Recently active (last-logged) people card. Defaults to `true`. */
  showLastLogged?: boolean;
  /** Render the "Who's near you" map widget. Defaults to `true`. */
  showNearby?: boolean;
  /**
   * Render the small Live TV CTA above "Recently active".
   *
   * Hidden by default (2026-06-18) to reduce the public-facing surface of
   * Live TV — Indian payment gateways (Cashfree etc.) flag IPTV-style
   * channel aggregation as high-risk and reject merchant accounts that
   * expose it from the main app chrome. The component + route remain so
   * the feature can be re-surfaced later; pass `showLiveTv` to opt back in.
   */
  showLiveTv?: boolean;
  /** Render the rail-bottom AdSense banner. Defaults to `true`. */
  showAd?: boolean;
  /** Render the "Upcoming events" widget (Phase 1 Local Events). Defaults to `true`. */
  showEvents?: boolean;
  /** Render the "Today's Yaariplay" widget (Phase 1 daily word game). Defaults to `true`. */
  showYaariplay?: boolean;
}

export function RightRailWidgets({
  showTrending = true,
  showSuggested = true,
  showLastLogged = true,
  showNearby = true,
  showLiveTv = false,
  showAd = true,
  showEvents = true,
  showYaariplay = true,
}: RightRailWidgetsProps = {}) {
  return (
    <div className="space-y-4">
      {showLiveTv ? <LiveTVCta /> : null}
      {showYaariplay ? <YaariplayWidget /> : null}
      {showLastLogged ? <LastLoggedUsers /> : null}
      {showTrending ? <TrendingHashtags /> : null}
      {showEvents ? <NearbyEvents /> : null}
      {showNearby ? <WhosNearYou /> : null}
      {showSuggested ? <SuggestedPeople /> : null}
      {showAd ? (
        <AdsenseSlot {...AdSlots.display} placeholderHeight={300} />
      ) : null}
    </div>
  );
}

/**
 * NearbyEvents — right-rail "Upcoming events" widget.
 *
 * Three-stage fallback so the widget almost never shows an empty state:
 *   1. 25 km nearby — events in the viewer's own city.
 *   2. 200 km nearby — widens to neighbouring cities (e.g. a Moga user
 *      picks up Ludhiana + Amritsar + Chandigarh).
 *   3. Same-country discover feed — any upcoming event in the viewer's
 *      country, no distance constraint.
 *
 * Geo source priority: IP geo (useViewerGeo) → user's saved map_lat/
 * map_lng → Delhi fallback for anonymous visitors. Country similarly
 * prefers IP → user profile → IN.
 */
/** Mobile/tablet "Upcoming events" grid — same 3-stage city → nearby →
 *  country fallback as the right-rail `NearbyEvents`, but renders the
 *  winning events as full `<EventCard>` tiles in a responsive grid that
 *  mirrors `LatestBlogs` exactly (2 cols ≤640, 3 cols 641-900, 4 cols
 *  901-1024). Mounted only when the right rail isn't on screen (viewport
 *  < lg) so the geo fetches don't duplicate. */
export function MobileNearbyEvents() {
  const user = useAuth((s) => s.user);
  const geo = useViewerGeo();
  const lat = geo.data?.lat ?? user?.map_lat ?? 28.6139;
  const lng = geo.data?.lng ?? user?.map_lng ?? 77.209;
  const country = geo.data?.country || user?.country || 'IN';

  const LIMIT = 4;    // match LatestBlogs row-size (hidden extras per viewport)
  const stage1 = useNearbyEvents({ lat, lng, radiusKm: 50, limit: LIMIT });
  const stage1Empty = !stage1.isLoading && stage1.data != null && stage1.data.events.length === 0;
  const stage2 = useNearbyEvents({
    lat: stage1Empty ? lat : null,
    lng: stage1Empty ? lng : null,
    radiusKm: 200,
    limit: LIMIT,
  });
  const stage2Empty = stage1Empty && !stage2.isLoading && stage2.data != null && stage2.data.events.length === 0;
  const stage3 = useDiscoverEvents(
    stage2Empty ? { country, limit: LIMIT, range: 'all' } : { limit: LIMIT, range: 'all', country: null },
  );

  let events: PlaceEvent[] = [];
  let label: 'city' | 'nearby' | 'country' | 'none' = 'none';
  if ((stage1.data?.events?.length ?? 0) > 0) {
    events = stage1.data!.events;
    label = 'city';
  } else if ((stage2.data?.events?.length ?? 0) > 0) {
    events = stage2.data!.events;
    label = 'nearby';
  } else {
    const stage3Flat = (stage3.data?.pages ?? []).flatMap((p) => p.events);
    if (stage3Flat.length > 0 && stage2Empty) {
      events = stage3Flat.slice(0, LIMIT);
      label = 'country';
    }
  }

  const isResolving =
    stage1.isLoading
    || (stage1Empty && stage2.isLoading)
    || (stage2Empty && stage3.isLoading);

  // Quiet-hide when the whole chain lands empty — supplementary section,
  // same policy as LatestBlogs.
  if (!isResolving && events.length === 0) return null;

  const countryLabel = (() => {
    if (!country) return 'your country';
    try {
      const names = new Intl.DisplayNames(['en'], { type: 'region' });
      return names.of(country) || country;
    } catch {
      return country;
    }
  })();

  return (
    <section className="mb-6" aria-label="Upcoming events">
      <header className="mb-2 flex items-center justify-between">
        <h2 className="text-base font-semibold text-ink">Upcoming events</h2>
        <Link
          to="/events"
          className="text-xs font-medium text-brand-700 hover:underline"
        >
          View all
        </Link>
      </header>
      {label === 'country' ? (
        <p className="mb-2 text-[11px] text-muted-foreground">No events near you — showing from {countryLabel}</p>
      ) : null}
      {isResolving && events.length === 0 ? (
        <p className="text-sm text-muted-foreground">Looking for events…</p>
      ) : (
        /* Grid mirrors LatestBlogs: hide extras per viewport so the row
           count maps to 2/3/4/3 cards depending on width. */
        <ul className="grid grid-cols-2 gap-2 min-[641px]:grid-cols-3 min-[901px]:grid-cols-4 min-[991px]:gap-4 min-[1025px]:grid-cols-3 max-[640px]:[&>*:nth-child(3)]:hidden max-[900px]:[&>*:nth-child(4)]:hidden min-[1025px]:[&>*:nth-child(4)]:hidden">
          {events.slice(0, LIMIT).map((e) => (
            <EventCard key={e.id} event={e} />
          ))}
        </ul>
      )}
    </section>
  );
}

export function NearbyEvents() {
  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4 text-brand-700" /> Upcoming events
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-1">
        <NearbyEventsInner />
      </CardContent>
    </Card>
  );
}

function NearbyEventsInner() {
  const user = useAuth((s) => s.user);
  const geo = useViewerGeo();
  // Resolve coords: IP geo first, then user profile, then Delhi.
  const lat = geo.data?.lat ?? user?.map_lat ?? 28.6139;
  const lng = geo.data?.lng ?? user?.map_lng ?? 77.209;
  // Resolve country for the final discover-feed fallback.
  const country = geo.data?.country || user?.country || 'IN';

  // Stage 1 — close-in (50 km). Matches the /events page rail's primary
  // bucket so the two surfaces behave consistently: Indian metros often
  // sprawl past the 25 km mark (Mumbai, Bengaluru), so a 50 km first
  // bucket catches the "events in my city" case more reliably.
  const stage1 = useNearbyEvents({ lat, lng, radiusKm: 50, limit: 3 });
  const stage1Empty = !stage1.isLoading && stage1.data != null && stage1.data.events.length === 0;

  // Stage 2 — widen to 200 km (fires only when stage 1 landed empty).
  const stage2 = useNearbyEvents({
    lat: stage1Empty ? lat : null,
    lng: stage1Empty ? lng : null,
    radiusKm: 200,
    limit: 3,
  });
  const stage2Empty = stage1Empty && !stage2.isLoading && stage2.data != null && stage2.data.events.length === 0;

  // Stage 3 — any event in the same country (fires only when 200 km empty).
  const stage3 = useDiscoverEvents(
    stage2Empty ? { country, limit: 3, range: 'all' } : { limit: 3, range: 'all', country: null },
  );
  // The stage3 hook always runs (useInfiniteQuery can't be enable-gated
  // the same way useQuery can). We just ignore its results until we need
  // them, and swapping params between an empty call and the real one is
  // cheap because the queryKey changes only when stage2Empty flips.

  // Pick which stage's results to render. First non-empty wins.
  let events: PlaceEvent[] = [];
  let label: 'city' | 'nearby' | 'country' | 'none' = 'none';
  if ((stage1.data?.events?.length ?? 0) > 0) {
    events = stage1.data!.events;
    label = 'city';
  } else if ((stage2.data?.events?.length ?? 0) > 0) {
    events = stage2.data!.events;
    label = 'nearby';
  } else {
    const stage3Flat = (stage3.data?.pages ?? []).flatMap((p) => p.events);
    if (stage3Flat.length > 0 && stage2Empty) {
      events = stage3Flat.slice(0, 3);
      label = 'country';
    }
  }

  // Loading state covers the whole chain so the widget doesn't flash
  // "No events" before the fallbacks have had a chance to resolve.
  const isResolving =
    stage1.isLoading
    || (stage1Empty && stage2.isLoading)
    || (stage2Empty && stage3.isLoading);

  // Resolve the ISO country code to a localised display name for the
  // stage-3 caption. Falls back to the raw code (or "your country") on
  // environments without Intl.DisplayNames support.
  const countryLabel = (() => {
    if (!country) return 'your country';
    try {
      const names = new Intl.DisplayNames(['en'], { type: 'region' });
      return names.of(country) || country;
    } catch {
      return country;
    }
  })();

  return (
    <div className="pb-1">
      {label === 'country' ? (
        <p className="mb-2 text-[11px] text-muted-foreground">No events near you — showing from {countryLabel}</p>
      ) : null}
      {isResolving ? (
        <p className="text-sm text-muted-foreground">Looking for events…</p>
      ) : events.length === 0 ? (
        <p className="rounded-md border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
          No upcoming events found.
        </p>
      ) : (
        <EventsList
          lat={null}
          lng={null}
          limit={3}
          variant="compact"
          emptyText=""
          events={events}
        />
      )}
      <Link to="/events" className="mt-2 block text-right text-xs font-medium text-brand-700 hover:underline">
        Browse all events →
      </Link>
    </div>
  );
}
